import React, { useEffect, useRef, useState } from 'react';
import { Modal, Box, Button, Typography, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import SignatureCanvas from 'react-signature-canvas';
import ClearIcon from '@mui/icons-material/Clear';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: '800px',
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: '12px',
  p: 4,
  maxHeight: '90vh',
  overflowY: 'auto',
};

const SignatureBox = styled(Box)(() => ({
  border: '2px solid #e0e0e0',
  borderRadius: '8px',
  backgroundColor: '#ffffff',
  marginTop: '16px',
  marginBottom: '16px',
  position: 'relative',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  overflow: 'hidden',
}));

const SignatureCanvasWrapper = styled('div')({
  width: '100%',
  cursor: 'crosshair',
  // Keep drawing coordinates LTR — parent RTL flips pointer X ("starts on the right")
  direction: 'ltr',
  '& canvas': {
    borderRadius: '8px',
    display: 'block',
    width: '100%',
    height: '200px',
    touchAction: 'none',
  },
});

const ActionButton = styled(Button)(({ variant }) => ({
  borderRadius: '8px',
  padding: '10px 24px',
  fontSize: '14px',
  fontWeight: '600',
  textTransform: 'none',
  minWidth: '120px',
  height: '42px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  ...(variant === 'send' && {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#ffffff',
    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
    '&:hover': {
      background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
    },
    '&:disabled': {
      background: '#cccccc',
      color: '#666666',
    },
  }),
  ...(variant === 'clear' && {
    background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
    color: '#ffffff',
    boxShadow: '0 2px 8px rgba(255, 107, 107, 0.3)',
    '&:hover': {
      background: 'linear-gradient(135deg, #ee5a6f 0%, #d94960 100%)',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(255, 107, 107, 0.4)',
    },
  }),
  ...(variant === 'cancel' && {
    background: 'transparent',
    color: '#666666',
    border: '2px solid #e0e0e0',
    '&:hover': {
      background: '#f5f5f5',
      borderColor: '#cccccc',
    },
  }),
}));

const SignatureModal = ({ open, handleClose, onSendWithSignature, reportPreview, confirmLabel }) => {
  const { t, i18n } = useTranslation();
  const direction = i18n.dir();
  const signatureRef = useRef(null);
  const containerRef = useRef(null);
  const [hasSignature, setHasSignature] = useState(false);
  const [signaturePreview, setSignaturePreview] = useState(null);
  /** Remount pad when opened so canvas bitmap matches wrapper width (same idea as DocumentSettingsDialog). */
  const [padKey, setPadKey] = useState(0);

  const clearSignature = () => {
    signatureRef.current?.clear();
    setHasSignature(false);
    setSignaturePreview(null);
  };

  const syncCanvasSize = () => {
    const pad = signatureRef.current;
    const container = containerRef.current;
    if (!pad || !container) return;

    const canvas = pad.getCanvas?.() || pad._canvas;
    if (!canvas) return;

    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const width = container.offsetWidth || container.clientWidth;
    const height = 200;
    if (!width) return;

    canvas.width = width * ratio;
    canvas.height = height * ratio;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    }
    pad.clear();
  };

  useEffect(() => {
    if (!open) return undefined;

    setHasSignature(false);
    setSignaturePreview(null);
    setPadKey((k) => k + 1);

    const timer = window.setTimeout(() => {
      syncCanvasSize();
    }, 50);

    const onResize = () => {
      const wasEmpty = signatureRef.current?.isEmpty?.() ?? true;
      syncCanvasSize();
      if (wasEmpty) {
        setHasSignature(false);
        setSignaturePreview(null);
      }
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSignatureEnd = () => {
    const isEmpty = signatureRef.current?.isEmpty();
    setHasSignature(!isEmpty);

    if (!isEmpty) {
      const dataUrl = signatureRef.current?.toDataURL('image/png');
      setSignaturePreview(dataUrl);
    }
  };

  const handleSend = async () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      return;
    }

    const signatureDataUrl = signatureRef.current.toDataURL('image/png');
    await onSendWithSignature(signatureDataUrl);
    clearSignature();
    handleClose();
  };

  const handleModalClose = () => {
    clearSignature();
    handleClose();
  };

  return (
    <Modal open={open} onClose={handleModalClose}>
      <Box sx={modalStyle} dir={direction}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: '#333' }}>
            {t('DigitalSignature') || 'Digital Signature'}
          </Typography>
          <IconButton onClick={handleModalClose} sx={{ color: '#666' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography variant="body1" sx={{ mb: 2, color: '#666' }}>
          {t('SignatureInstructions') || 'Please sign below to authorize this report before sending it to the client.'}
        </Typography>

        {reportPreview && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              border: '1px solid #e0e0e0',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#333' }}>
              {t('ReportPreview') || 'Report Preview:'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666' }}>
              {reportPreview}
            </Typography>
          </Box>
        )}

        {signaturePreview && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              backgroundColor: '#fff',
              borderRadius: '8px',
              border: '2px solid #4CAF50',
              boxShadow: '0 2px 8px rgba(76, 175, 80, 0.2)',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#4CAF50' }}>
              ✓ {t('SignaturePreview') || 'Preview - How it will appear in the document:'}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                borderTop: '2px solid #333',
                pt: 2,
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ flex: 1, minWidth: '200px', textAlign: 'center' }}>
                <img
                  src={signaturePreview}
                  alt="Signature Preview"
                  style={{
                    maxWidth: '250px',
                    maxHeight: '80px',
                    border: '1px solid #e0e0e0',
                    padding: '5px',
                    background: 'white',
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mt: 1,
                    pt: 1,
                    borderTop: '2px solid #000',
                    fontWeight: 600,
                  }}
                >
                  {t('AuthorizedSignature') || 'Authorized Signature'}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: '200px', textAlign: 'center' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {new Date().toLocaleDateString('he-IL', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    pt: 1,
                    borderTop: '2px solid #000',
                    fontWeight: 600,
                  }}
                >
                  {t('Date') || 'Date'}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        <SignatureBox>
          {/* dir=ltr matches DocumentSettingsDialog drawing behavior under Hebrew RTL UI */}
          <SignatureCanvasWrapper ref={containerRef} dir="ltr">
            <SignatureCanvas
              key={padKey}
              ref={signatureRef}
              canvasProps={{
                className: 'signature-canvas',
                // Same pattern as DocumentSettingsDialog: CSS size only, no fixed bitmap width
                style: {
                  border: 'none',
                  borderRadius: '8px',
                  width: '100%',
                  height: '200px',
                  backgroundColor: '#ffffff',
                  touchAction: 'none',
                },
              }}
              onEnd={handleSignatureEnd}
              backgroundColor="#ffffff"
            />
          </SignatureCanvasWrapper>

          <Box
            sx={{
              borderTop: '2px dashed #cccccc',
              mt: '-2px',
              position: 'relative',
            }}
          />
        </SignatureBox>

        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#999', mb: 3 }}>
          {t('SignAboveLine') || 'Sign above the line'}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
          }}
        >
          <ActionButton variant="cancel" onClick={handleModalClose}>
            {t('Cancel') || 'Cancel'}
          </ActionButton>

          <ActionButton
            variant="clear"
            onClick={clearSignature}
            startIcon={<ClearIcon />}
            disabled={!hasSignature}
          >
            {t('ClearSignature') || 'Clear'}
          </ActionButton>

          <ActionButton
            variant="send"
            onClick={handleSend}
            startIcon={<SendIcon />}
            disabled={!hasSignature}
          >
            {confirmLabel || t('SignAndSend') || 'Sign & Send'}
          </ActionButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default SignatureModal;
